// src/riskmonitors/ScenarioMonitor.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeSelectNarrative, selectRiskEntries, selectAllControls } from './redux/selectors/assessmentSelectors';
import { selectScenarioMonitor } from './redux/selectors/monitorSelectors';
import { runScenarioMonitor, clearScenarioMonitor } from './redux/actions/monitorActions';
import { Box, Paper, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Dialog, DialogTitle, DialogContent,  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell, } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import NarrativeScenarioReport from './NarrativeScenarioReport';
import IconWithProgress from './IconWithProgress';


export default function ScenarioAnalyzer({ modelId }) {
  const dispatch = useDispatch();

  // Available narrative sections
  const sections = ['useCase', 'businessContext', 'modelContext'];
  // User-selected section to analyze
  const [selectedSection, setSelectedSection] = useState('useCase');

  const originalNarrative = useSelector(state => makeSelectNarrative(selectedSection)(state, modelId));
  const entries           = useSelector(state => selectRiskEntries(state, modelId));
  const controls          = useSelector(state => Object.values(selectAllControls(state)));
  //const monitor           = useSelector(s => s.monitors[modelId] || {});
  const { loading, data, error } = useSelector(state => selectScenarioMonitor(state, modelId));

  // Local edit buffer
  const [updatedNarrative, setUpdatedNarrative] = useState(originalNarrative);
  useEffect(() => {
    setUpdatedNarrative(originalNarrative);
  }, [originalNarrative, selectedSection]);

  // For showing the report dialog
  const [showReport, setShowReport] = useState(false);

  const toTitleCase = (str) => {
    if (!str) return '';
    // Handle known acronyms
    str = str.replace(/\b(EU|ROC|AUC)\b/g, match => match.toUpperCase());
  
    // Format camelCase and PascalCase
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before capitals
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // Handle sequences of capitals
      .replace(/\b([Aa]nd)\b/g, '&')
      .split(/\s+/) // Split by spaces
      .map(word => {
        if (['EU', 'ROC', 'AUC'].includes(word)) {
          return word; // Return acronyms unchanged
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(); // Capitalize each word
        }
      })
      .join(' ')
      .trim(); // Trim extra spaces
  };

  const handleRun = () => {
    dispatch(runScenarioMonitor({
      modelId,
      section: selectedSection,
      entries,
      controls,
      originalNarrative,
      updatedNarrative
    }));
  };

  if (data) {
    const { updateRisks = [], newRisks = [], updateCtrls = [], newCtrls = [] } = data.report;
    return (
      <Box p={2}>
        <Paper variant="outlined" sx={{ p:2, display:'flex', flexDirection:'column', gap:2 }}>
        <Typography variant="h6">Scenario Summary</Typography>
          <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
            {toTitleCase(selectedSection)}
          </Typography>
          <Table size="small" sx={{ my: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>New Risks</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Updated Risks</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Controls Changed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell align="center">
                <Chip label={newRisks.length} color="warning" />
              </TableCell>
              <TableCell align="center">
                <Chip label={updateRisks.length} color="warning" />
              </TableCell>
              <TableCell align="center">
                <Chip label={updateCtrls.length + newCtrls.length} color="info" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box sx={{ display: 'flex',  justifyContent: 'center', gap: 1 }}>
          <Button onClick={() => setShowReport(true)}>
            VIEW REPORT
          </Button>
          <Button
            startIcon={<ReplayIcon />}
            onClick={() => dispatch(clearScenarioMonitor(modelId))}
            disabled={loading}
          >
            CLEAR
          </Button>
        </Box>
        </Paper>
        <Dialog open={showReport} onClose={() => setShowReport(false)} fullWidth maxWidth="md">
          <DialogTitle>Scenario Impact Report</DialogTitle>
          <DialogContent dividers>
           <NarrativeScenarioReport modelId={modelId} onClose={() => setShowReport(false)} />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {/* Section selector UI */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="section-select-label">Select Section</InputLabel>
        <Select
          labelId="section-select-label"
          value={selectedSection}
          label="Select Section"
          onChange={e => setSelectedSection(e.target.value)}
        >
          {sections.map(sec => (
            <MenuItem key={sec} value={sec}>
              {sec === 'useCase' ? 'Use Case' : sec === 'businessContext' ? 'Business Context' : 'Model Context'}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="subtitle1">Original Narrative</Typography>
      <Typography mb={2}>{originalNarrative || 'No narrative yet.'}</Typography>

      <TextField
        label="Edit Narrative"
        multiline
        minRows={4}
        fullWidth
        variant="outlined"
        value={updatedNarrative}
        onChange={e => setUpdatedNarrative(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Button
        variant="outlined"
        disabled={!originalNarrative || loading}
        onClick={handleRun}
        startIcon={<IconWithProgress isLoading={loading} />}
      >
        Analyze Scenario
      </Button>
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
}